import React from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import CargomaticSelect from 'components/v2/select';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import CargomaticInput from 'components/v2/input';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import {
  lengthUnitsOptions,
  loadSchema,
  loadTypesOptions,
  weightUnitsOptions,
} from '../../utils/localization/types';
import { sluggify } from '../../utils/localization/utils';

export const getLoadTypeText = (quantity, type) => {
  if (quantity !== 1) {
    return type;
  }
  return type === 'pallets' ? 'pallet' : 'box';
};

export function getLoadQuantity(load) {
  const t = i18next.t;
  const { type, quantity } = load;
  let quantityText;
  const typeText = getLoadTypeText(quantity, type);
  if (typeof quantity !== 'undefined') {
    quantityText = `${quantity} ${titleize(t(sluggify(typeText)))}`;
  } else {
    quantityText = 'N/A';
  }

  return quantityText;
}

export function getLoadDimensions(load) {
  const { length, width, height, dimensional_units } = load;
  let dimensionsText;
  if (
    typeof length !== 'undefined' &&
    typeof width !== 'undefined' &&
    typeof height !== 'undefined'
  ) {
    dimensionsText = `(${length}x${width}x${height} ${dimensional_units})`;
  } else {
    dimensionsText = 'N/A';
  }
  return dimensionsText;
}

export function getLoadWeight(load) {
  const { weight, weight_units } = load;
  let weightText;
  if (typeof weight !== 'undefined') {
    weightText = `${weight} ${weight_units}`;
  } else {
    weightText = 'N/A';
  }
  return weightText;
}

export function getLoadText(load) {
  const quantityText = getLoadQuantity(load);
  const dimensionsText = getLoadDimensions(load);
  const weightText = getLoadWeight(load);

  return `${quantityText} ${dimensionsText} - ${weightText}`;
}

const Loads = ({ loadOnly, index, load, onLoadFieldChange, onRemoveLoad }) => {
  const { t } = useTranslation();
  const tooltip = <Tooltip id="remove-upload-tooltip"> {t('REMOVE')} </Tooltip>;

  // miles and kilometers does not make sense when you want to measure a payload. It's unlikely that anyone would use us survey unit as well
  const notAllowedLengthUnitsForPayload = ['km', 'mi', 'ft-us'];
  const payloadLengthUnitsOptions = lengthUnitsOptions.filter(
    (unit) => !notAllowedLengthUnitsForPayload.includes(unit.value),
  );

  return (
    <tr>
      <td className="">
        <CargomaticSelect
          showCaption
          selected={load.type}
          className="w-28"
          items={loadTypesOptions}
          onSelect={(item) => {
            onLoadFieldChange('type', item.value);
          }}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
          hideLabel
        />
      </td>
      <td className="" style={{ width: '120px' }}>
        <CargomaticInput
          type="number"
          placeholder={t('QUANTITY')}
          min={1}
          className="w-16"
          value={load.quantity}
          onChange={(value) => {
            onLoadFieldChange('quantity', value);
          }}
          hideLabel
          applyDefaultMargins={false}
        />
      </td>
      <td className="" style={{ width: '120px' }}>
        <CargomaticInput
          type="number"
          className="w-16"
          placeholder={t('LENGTH')}
          min={1}
          value={load.length}
          onChange={(value) => {
            onLoadFieldChange('length', value);
          }}
          hideLabel
          applyDefaultMargins={false}
        />
      </td>
      <td className="" style={{ width: '120px' }}>
        <CargomaticInput
          className="w-16"
          type="number"
          placeholder={t('WIDTH')}
          min={1}
          value={load.width}
          onChange={(value) => {
            onLoadFieldChange('width', value);
          }}
          hideLabel
          applyDefaultMargins={false}
        />
      </td>
      <td className="" style={{ width: '120px' }}>
        <CargomaticInput
          type="number"
          className="w-16"
          placeholder={t('HEIGHT')}
          min={1}
          value={load.height}
          onChange={(value) => {
            onLoadFieldChange('height', value);
          }}
          hideLabel
          applyDefaultMargins={false}
        />
      </td>
      <td className="" style={{ width: '120px' }}>
        <CargomaticSelect
          showCaption
          selected={load.dimensional_units}
          className="w-20"
          items={payloadLengthUnitsOptions}
          onSelect={(item) => {
            onLoadFieldChange('dimensional_units', item.value);
          }}
          disabled={index > 0}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
          hideLabel
        />
      </td>
      {!loadOnly && (
        <td className="" style={{ width: '120px' }}>
          <CargomaticInput
            type="number"
            className="w-20"
            placeholder={t('WEIGHT')}
            min={1}
            value={load.weight}
            onChange={(value) => {
              onLoadFieldChange('weight', value);
            }}
            hideLabel
            applyDefaultMargins={false}
          />
        </td>
      )}
      {!loadOnly && (
        <td className="" style={{ width: '120px' }}>
          <CargomaticSelect
            showCaption
            selected={load.weight_units}
            className="w-20"
            items={weightUnitsOptions}
            onSelect={(item) => {
              onLoadFieldChange('weight_units', item.value);
            }}
            disabled={index > 0}
            applyDefaultMargins={false}
            valueKey="value"
            textKey="localizationSlug"
            hideLabel
          />
        </td>
      )}
      <td
        className=""
        style={{ width: '120px', textAlign: 'center', verticalAlign: 'middle' }}
      >
        {load.type === 'pallets' && (
          <CargomaticInput
            type="checkbox"
            id={`stackable-${index}`}
            hideLabel
            className="flex items-center"
            info={null}
            value={load.stackable}
            disabled={load.type !== 'pallets'}
            applyDefaultMargins={false}
            onChange={(value) => {
              onLoadFieldChange('stackable', value);
            }}
          />
        )}
        {load.type !== 'pallets' && <span>&nbsp;</span>}
      </td>
      <td>
        <OverlayTrigger placement="top" overlay={tooltip}>
          <DeleteButton onClick={() => onRemoveLoad()} />
        </OverlayTrigger>
      </td>
    </tr>
  );
};

Loads.propTypes = {
  index: PropTypes.number.isRequired,
  load: PropTypes.shape({
    ...loadSchema,
  }).isRequired,
  onLoadFieldChange: PropTypes.func.isRequired,
  onRemoveLoad: PropTypes.func.isRequired,
};

export default Loads;
